<template>
    <div id="templateOptionsModal" class="modal fade" ref="exampleModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-scrollable modal-xl" role="document">
            <div class="modal-content" v-if="data">
                <div class="modal-header">
                    <h5 class="modal-title">{{ $t('components.templateOptions.title') }}</h5>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-ghost-secondary btn-icon btn-sm rounded-circle" @click="hideModal">
                            <i class="bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <div class="modal-body" style="padding: 0 !important">
                    <div v-if="currentStep === 1" class="step-content">
                        <div class="header-selection-container">
                            <div class="alert alert-soft-primary">
                                <i class="bi-info-circle me-2"></i>
                                {{ $t('components.templateOptions.headerSelectionHelp') }}
                            </div>
                            <div class="preview-table-container">
                                <table class="table table-bordered table-hover">
                                    <tbody>
                                        <tr style="cursor: pointer" v-for="(row, index) in previewRows" :key="index" :class="{ 'selected-header': headingRow === index + 1 }" @click="selectHeaderRow(index + 1)">
                                            <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                                                {{ cell }}
                                            </td>
                                            <td v-if="headingRow === index + 1" class="check-column">
                                                <i class="bi-check-circle-fill text-success"></i>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Column Mapping Step -->
                    <div v-if="currentStep === 2" class="step-content">
                        <div class="mapping-container">
                            <div class="source-columns">
                                <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h6>{{ $t('components.templateOptions.availableColumns') }}</h6>
                                    <div class="detected-count" v-if="autoDetectEnabled">
                                        <span class="badge bg-soft-success"> {{ Object.keys(detectedColumns).length }} {{ $t('components.templateOptions.columnsDetected') }} </span>
                                    </div>
                                </div>

                                <!-- Column Search -->
                                <div class="search-box mb-3">
                                    <div class="input-group">
                                        <span class="input-group-text">
                                            <i class="bi-search"></i>
                                        </span>
                                        <input type="text" class="form-control form-control-sm" v-model="columnSearch" :placeholder="$t('components.templateOptions.searchColumns')" />
                                        <button v-if="columnSearch" class="btn btn-ghost-secondary btn-sm" @click="columnSearch = ''">
                                            <i class="bi-x-lg"></i>
                                        </button>
                                    </div>
                                </div>

                                <div class="column-list" ref="sourceColumns">
                                    <div
                                        v-for="column in filteredColumns"
                                        :key="column"
                                        class="column-item"
                                        :class="{
                                            detected: isColumnDetected(column),
                                            'type-number': detectColumnType(column) === 'number',
                                            'type-text': detectColumnType(column) === 'text',
                                            'type-date': detectColumnType(column) === 'date',
                                            'is-empty': !column || column.trim() === '',
                                        }"
                                        :draggable="column && column.trim() !== ''"
                                        @dragstart="column && column.trim() !== '' && dragStart($event, column)"
                                    >
                                        {{ column && column.trim() !== '' ? column : 'Boş' }}
                                        <div class="column-meta" v-if="column && column.trim() !== ''">
                                            <span class="column-type">{{ detectColumnType(column) }}</span>
                                            <i class="bi-check-circle-fill text-success ms-2" v-if="isColumnDetected(column)"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mapping-fields">
                                <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h6>{{ $t('components.templateOptions.requiredFields') }}</h6>
                                    <button class="btn btn-ghost-secondary btn-xs" @click="resetMapping">
                                        <i class="bi-arrow-counterclockwise me-1"></i>
                                        {{ $t('components.templateOptions.resetMapping') }}
                                    </button>
                                </div>

                                <draggable v-model="orderedMappingFields" class="field-list" handle=".field-handle" @start="drag = true" @end="drag = false" item-key="key">
                                    <template #item="{ element }">
                                        <div
                                            class="field-item"
                                            :class="{
                                                mapped: defaults[element.key],
                                                required: element.required,
                                                'auto-detected': isAutoDetected(element.key),
                                                'type-mismatch': hasTypeMismatch(element.key),
                                            }"
                                            @dragover.prevent
                                            @drop="handleDrop($event, element.key)"
                                        >
                                            <div class="field-handle">
                                                <i class="bi-grip-vertical"></i>
                                            </div>
                                            <div class="field-content">
                                                <span class="field-label">
                                                    {{ element.label }}
                                                    <span class="required-badge" v-if="element.required">*</span>
                                                    <span class="type-badge" v-if="element.type">{{ element.type }}</span>
                                                </span>
                                                <span class="field-value">
                                                    {{ defaults[element.key] || $t('components.templateOptions.dragHere') }}
                                                    <i class="bi-magic text-primary ms-2" v-if="isAutoDetected(element.key)"></i>
                                                </span>
                                            </div>
                                            <div class="field-actions" v-if="defaults[element.key]">
                                                <button class="btn btn-xs btn-ghost-danger" @click="clearMapping(element.key)">
                                                    <i class="bi-x-lg"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>
                    </div>

                    <!-- Preview Step -->
                    <div v-if="currentStep === 3" class="step-content">
                        <div class="preview-container" style="padding: 1rem">
                            <div class="preview-actions d-flex justify-content-between align-items-center mb-3">
                                <div class="alert alert-soft-success" style="width: 80%; margin: 0" v-if="isAllFieldsMapped && !hasValidationErrors">
                                    <i class="bi-check-circle me-2"></i>
                                    {{ $t('components.templateOptions.allFieldsMapped') }}
                                </div>
                                <div class="d-flex gap-2">
                                    <button class="btn btn-soft-primary btn-sm" @click="applyBulkFixes" v-if="hasValidationErrors" :disabled="isApplyingFixes">
                                        <i class="bi-magic me-1"></i>
                                        {{ $t('components.templateOptions.applyBulkFixes') }}
                                    </button>
                                </div>
                                <div class="preview-controls">
                                    <button class="btn btn-soft-secondary btn-sm" @click="toggleFullPreview">
                                        <i :class="showFullPreview ? 'bi-arrows-collapse' : 'bi-arrows-expand'" class="me-1"></i>
                                        {{ showFullPreview ? $t('components.templateOptions.showLess') : $t('components.templateOptions.showAll') }}
                                    </button>
                                </div>
                            </div>

                            <div class="alert alert-soft-warning mb-3" v-if="!isAllFieldsMapped">
                                <i class="bi-exclamation-triangle me-2"></i>
                                {{ $t('components.templateOptions.missingFields') }}
                            </div>
                            <div class="alert alert-soft-danger mb-3" v-if="hasValidationErrors">
                                <div class="d-flex align-items-center mb-2">
                                    <i class="bi-x-circle me-2"></i>
                                    {{ $t('components.templateOptions.validationErrors') }}
                                    <strong class="ms-1"> ({{ totalErrorCount }} {{ $t('components.templateOptions.rowsWithErrors') }}) </strong>
                                </div>
                                <div class="validation-summary">
                                    <div v-for="(errors, field) in validationErrors" :key="field" class="validation-field-count">
                                        <span class="field-name">{{ mappingFields[field].label }}:</span>
                                        <span class="error-count">{{ errors.length }} {{ $t('components.templateOptions.invalidRows') }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Validation Errors Summary -->
                            <div class="validation-errors mb-4" v-if="hasValidationErrors">
                                <div class="validation-error-group" v-for="(errors, field) in validationErrors" :key="field">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h6 class="validation-error-title">{{ mappingFields[field].label }} {{ $t('components.templateOptions.validationIssues') }}</h6>
                                        <button class="btn btn-xs btn-soft-primary" @click="applyFieldFixes(field)" :disabled="isApplyingFixes">
                                            {{ $t('components.templateOptions.fixField') }}
                                        </button>
                                    </div>
                                    <ul class="validation-error-list">
                                        <li v-for="(error, index) in errors" :key="index">
                                            {{ $t('components.templateOptions.row') }} {{ error.row }}:
                                            <span class="invalid-value">{{ error.value }}</span>
                                            <span class="arrow-icon mx-2">→</span>
                                            <span class="fixed-value" v-if="error.fixedValue">{{ error.fixedValue }}</span>
                                            <span class="text-muted">{{ error.message }}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div class="preview-table-wrapper">
                                <table class="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th class="row-number">#</th>
                                            <th v-for="(field, key) in mappedOrderedFields" :key="key">
                                                {{ field.label }}
                                                <span class="required-badge" v-if="field.required">*</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, rowIndex) in displayedData" :key="rowIndex">
                                            <td class="row-number">{{ rowIndex + 1 }}</td>
                                            <td
                                                v-for="(field, key) in mappedOrderedFields"
                                                :key="key"
                                                :class="{
                                                    'has-validation-error': hasFieldError(field.key, row[field.key]),
                                                    'is-required': field.required,
                                                    'is-fixed': isFixedValue(field.key, row[field.key], rowIndex),
                                                    'editable-cell': true,
                                                }"
                                            >
                                                <div class="cell-content" @click="startEditing(rowIndex, field.key)">
                                                    <template v-if="isEditing(rowIndex, field.key)">
                                                        <input type="text" v-model="row[field.key]" @blur="finishEditing(rowIndex, field.key)" @keyup.enter="finishEditing(rowIndex, field.key)" ref="editInput" class="form-control form-control-sm" />
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="isFixedValue(field.key, row[field.key], rowIndex)">
                                                            <span class="fixed-value">
                                                                {{ getFixedValue(field.key, row[field.key], rowIndex) }}
                                                            </span>
                                                        </template>
                                                        <template v-else>
                                                            {{ row[field.key] }}
                                                        </template>
                                                    </template>
                                                </div>
                                                <i class="bi-exclamation-circle text-danger ms-1" v-if="hasFieldError(field.key, row[field.key])" :title="getFieldErrorMessage(field.key, row[field.key])"></i>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Data Statistics -->
                            <div class="data-stats mt-4">
                                <div class="stats-grid">
                                    <div class="stat-item">
                                        <div class="stat-label">{{ $t('components.templateOptions.totalRows') }}</div>
                                        <div class="stat-value">{{ totalValidRows }}</div>
                                    </div>
                                    <div class="stat-item">
                                        <div class="stat-label">{{ $t('components.templateOptions.mappedFields') }}</div>
                                        <div class="stat-value">{{ Object.values(defaults).filter(Boolean).length }}/{{ Object.keys(mappingFields).length }}</div>
                                    </div>
                                    <div class="stat-item" v-if="hasValidationErrors">
                                        <div class="stat-label">{{ $t('components.templateOptions.validationIssues') }}</div>
                                        <div class="stat-value text-danger">
                                            {{ Object.values(validationErrors).reduce((acc, curr) => acc + curr.length, 0) }}
                                        </div>
                                    </div>
                                    <div class="stat-item" v-if="fixedValuesCount > 0">
                                        <div class="stat-label">{{ $t('components.templateOptions.fixedValues') }}</div>
                                        <div class="stat-value text-success">{{ fixedValuesCount }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Navigation Buttons -->
                    <div class="modal-footer">
                        <button class="btn btn-ghost-secondary" @click="prevStep" v-if="currentStep > 1">
                            {{ $t('common.back') }}
                        </button>
                        <button class="btn btn-primary" @click="nextStep" v-if="currentStep < 3">
                            {{ $t('common.next') }}
                        </button>
                        <button class="btn btn-primary" @click="handleSubmit" v-if="currentStep === 3" :disabled="!isAllFieldsMapped">
                            {{ $t('common.finish') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from 'bootstrap'
import draggable from 'vuedraggable'

export default {
    name: 'TemplateOptions',
    components: {
        draggable,
    },
    directives: {
        tooltip: {
            mounted(el, binding) {
                if (binding.value) {
                    el.setAttribute('title', binding.value)
                }
            },
            updated(el, binding) {
                if (binding.value) {
                    el.setAttribute('title', binding.value)
                }
            },
        },
    },
    props: {
        showModal: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
        identifierType: {
            type: String,
            default: 'ASIN',
        },
        mappingConfig: {
            type: Object,
            default: null,
        },
        file: {
            type: Array,
            default: null,
        },
    },
    emits: ['closeModal', 'update:data', 'saveTemplateOptions'],
    data: () => ({
        currentStep: 1,
        modalInstance: null,
        headingRow: 1,
        columns: [],
        autoDetectEnabled: true,
        validationRules: {},
        validationErrors: {},
        columnPatterns: {},
        defaults: {},
        mappingFields: {},
        columnSearch: '',
        drag: false,
        orderedMappingFields: [],
        columnTypes: {},
        showFullPreview: false,
        isApplyingFixes: false,
        fixedValues: {},
        editingCell: null,
        editedData: {},
        lastDataStr: null,
    }),
    computed: {
        previewRows() {
            // Show first 15 rows in the initial preview
            return this.data.slice(0, 15)
        },
        previewMappedData() {
            if (!this.data || !this.headingRow || !this.columns.length) return []

            // Get data after the header row
            const dataRows = this.data.slice(this.headingRow)

            // Return first 5 rows for preview
            return dataRows.slice(0, 5).map((row) => {
                const mappedRow = {}
                // Ensure row is treated as an array
                const rowArray = Array.isArray(row) ? row : Object.values(row)

                // Only map selected columns
                Object.entries(this.defaults).forEach(([field, columnName]) => {
                    if (columnName) {
                        // Only process if column is selected
                        const columnIndex = this.columns.indexOf(columnName)
                        if (columnIndex !== -1 && columnIndex < rowArray.length) {
                            mappedRow[field] = rowArray[columnIndex]
                        }
                    }
                })

                return mappedRow
            })
        },
        displayedData() {
            if (!this.data || !this.headingRow || !this.columns.length) return []

            // Get data after the header row
            const dataRows = this.data.slice(this.headingRow)

            // Önce zorunlu alanları kontrol ederek geçerli satırları filtrele
            const validRows = dataRows.filter((row) => {
                const rowArray = Array.isArray(row) ? row : Object.values(row)
                return Object.entries(this.mappingFields)
                    .filter(([_, field]) => field.required)
                    .every(([fieldKey, _]) => {
                        if (this.defaults[fieldKey]) {
                            const columnIndex = this.columns.indexOf(this.defaults[fieldKey])
                            if (columnIndex !== -1 && columnIndex < rowArray.length) {
                                const value = rowArray[columnIndex]
                                return value && value.toString().trim() !== ''
                            }
                        }
                        return false
                    })
            })

            // Map all valid rows
            const formattedData = validRows.map((row) => {
                const mappedRow = {}
                const rowArray = Array.isArray(row) ? row : Object.values(row)

                // Only map selected columns
                Object.entries(this.defaults).forEach(([field, columnName]) => {
                    if (columnName) {
                        const columnIndex = this.columns.indexOf(columnName)
                        if (columnIndex !== -1 && columnIndex < rowArray.length) {
                            mappedRow[field] = rowArray[columnIndex]
                        }
                    }
                })

                return mappedRow
            })

            return this.showFullPreview ? formattedData : formattedData.slice(0, 5)
        },
        isAllFieldsMapped() {
            if (this.hasValidationErrors) {
                return false
            }

            return Object.entries(this.mappingFields)
                .filter(([_, field]) => field.required)
                .every(([key]) => this.defaults[key])
        },
        detectedColumns() {
            if (!this.autoDetectEnabled || !this.columns.length) return {}

            const detected = {}
            const usedColumns = new Set() // Track which columns have been used

            // Sort fields by priority (required fields first)
            const sortedFields = Object.entries(this.columnPatterns).sort(([keyA], [keyB]) => {
                const isRequiredA = this.mappingFields[keyA]?.required
                const isRequiredB = this.mappingFields[keyB]?.required
                if (isRequiredA && !isRequiredB) return -1
                if (!isRequiredA && isRequiredB) return 1
                return 0
            })

            // First pass: exact matches
            this.columns.forEach((column) => {
                const columnLower = column.toLowerCase().trim()
                for (const [field, patterns] of sortedFields) {
                    if (!detected[field] && !usedColumns.has(column)) {
                        // Check for exact matches first
                        if (patterns.some((pattern) => pattern === columnLower)) {
                            detected[field] = column
                            usedColumns.add(column)
                            break
                        }
                    }
                }
            })

            // Second pass: partial matches
            this.columns.forEach((column) => {
                const columnLower = column.toLowerCase().trim()
                for (const [field, patterns] of sortedFields) {
                    if (!detected[field] && !usedColumns.has(column)) {
                        // Then check for partial matches
                        if (patterns.some((pattern) => columnLower.includes(pattern))) {
                            detected[field] = column
                            usedColumns.add(column)
                            break
                        }
                    }
                }
            })

            return detected
        },
        hasValidationErrors() {
            return Object.keys(this.validationErrors).length > 0
        },
        filteredColumns() {
            if (!this.columnSearch) return this.columns
            const search = this.columnSearch.toLowerCase().trim()
            return this.columns.filter((col) => {
                if (!col) return false
                const colStr = String(col).toLowerCase().trim()
                return colStr.includes(search)
            })
        },
        fixedValuesCount() {
            return Object.values(this.fixedValues).reduce((acc, field) => acc + Object.keys(field).length, 0)
        },
        totalErrorCount() {
            // Unique row numaralarını sayar
            const uniqueErrorRows = new Set()
            Object.values(this.validationErrors).forEach((errors) => {
                errors.forEach((error) => uniqueErrorRows.add(error.row))
            })
            return uniqueErrorRows.size
        },
        mappedOrderedFields() {
            return this.orderedMappingFields.filter((field) => this.defaults[field.key])
        },
        totalValidRows() {
            if (!this.data || !this.headingRow || !this.columns.length) return 0

            const dataRows = this.data.slice(this.headingRow)
            return dataRows.filter((row) => {
                const rowArray = Array.isArray(row) ? row : Object.values(row)
                return Object.entries(this.mappingFields)
                    .filter(([_, field]) => field.required)
                    .every(([fieldKey, _]) => {
                        if (this.defaults[fieldKey]) {
                            const columnIndex = this.columns.indexOf(this.defaults[fieldKey])
                            if (columnIndex !== -1 && columnIndex < rowArray.length) {
                                const value = rowArray[columnIndex]
                                return value && value.toString().trim() !== ''
                            }
                        }
                        return false
                    })
            }).length
        },
    },
    methods: {
        validateField(fieldKey, value) {
            // Eğer değer boş ve alan zorunlu değilse, geçerli kabul et
            if (!value && !this.mappingFields[fieldKey].required) {
                return true
            }

            // Eğer değer boş ve alan zorunluysa, geçersiz kabul et
            if (!value && this.mappingFields[fieldKey].required) {
                return false
            }

            if (!this.validationRules[fieldKey]) return true

            const rule = this.validationRules[fieldKey]
            const isValid = rule.pattern.test(value)

            // Additional validation logic for specific fields
            switch (fieldKey) {
                case 'product_cost':
                case 'price':
                    // Ensure the price is not zero or negative
                    if (isValid) {
                        const numValue = parseFloat(value)
                        return numValue >= 0
                    }
                    break
                case 'stock':
                    // Ensure stock is not negative
                    if (isValid) {
                        const numValue = parseInt(value)
                        return numValue >= 0
                    }
                    break
            }

            return isValid
        },
        validateMappedData() {
            this.validationErrors = {}
            const dataRows = this.data.slice(this.headingRow)
            const validRows = []

            // Önce zorunlu alanları kontrol et ve geçersiz satırları filtrele
            dataRows.forEach((row, rowIndex) => {
                const rowArray = Array.isArray(row) ? row : Object.values(row)
                let isRowValid = true

                // Zorunlu alanları kontrol et
                Object.entries(this.mappingFields)
                    .filter(([_, field]) => field.required)
                    .forEach(([fieldKey, _]) => {
                        if (this.defaults[fieldKey]) {
                            const columnIndex = this.columns.indexOf(this.defaults[fieldKey])
                            if (columnIndex !== -1 && columnIndex < rowArray.length) {
                                const value = rowArray[columnIndex]
                                if (!value || value.toString().trim() === '') {
                                    isRowValid = false
                                }
                            } else {
                                isRowValid = false
                            }
                        }
                    })

                if (isRowValid) {
                    validRows.push({ row: rowArray, index: rowIndex })
                }
            })

            // Geçerli satırlar için tüm alanları (zorunlu ve opsiyonel) valide et
            const finalValidRows = validRows.map(({ row: rowArray, index: rowIndex }) => {
                const hasErrors = Object.entries(this.defaults).some(([fieldKey, columnName]) => {
                    if (columnName) {
                        const columnIndex = this.columns.indexOf(columnName)
                        if (columnIndex !== -1 && columnIndex < rowArray.length) {
                            const value = rowArray[columnIndex]

                            // Boş opsiyonel alanları veya validasyon hatası olan alanları kontrol et
                            if (!this.validateField(fieldKey, value)) {
                                if (!this.validationErrors[fieldKey]) {
                                    this.validationErrors[fieldKey] = []
                                }
                                this.validationErrors[fieldKey].push({
                                    row: rowIndex + 1,
                                    value: value || '',
                                    message: !value ? this.$t('components.templateOptions.validation.emptyField') : this.$t(this.validationRules[fieldKey].message),
                                })
                                return this.mappingFields[fieldKey].required // Sadece zorunlu alanlardaki hatalar için true döndür
                            }
                        }
                    }
                    return false
                })

                return { rowArray, rowIndex, hasErrors }
            })

            // Başlık satırını ve geçerli satırları birleştir
            const headerRows = this.data.slice(0, this.headingRow)
            const filteredValidRows = finalValidRows
                .filter((row) => !row.hasErrors) // Zorunlu alan hatası olmayan satırları filtrele
                .map((row) => row.rowArray)

            // Veriyi güncelle
            this.$emit('update:data', [...headerRows, ...filteredValidRows])
        },
        selectHeaderRow(rowIndex) {
            this.headingRow = rowIndex
            // Ensure we get the header row correctly and handle null/empty values
            const headerRow = this.data[rowIndex - 1]
            if (Array.isArray(headerRow)) {
                // Filter out null/undefined values and convert to string
                this.columns = headerRow.map((col) => (col === null || col === undefined ? '' : String(col)))
            }
            // Reset any existing mappings when header row changes
            this.resetMapping()
            if (this.autoDetectEnabled) {
                this.applyAutoDetection()
            }
            // Validate data after header selection
            this.validateMappedData()
        },
        applyAutoDetection() {
            const detected = this.detectedColumns
            Object.entries(detected).forEach(([field, column]) => {
                if (!this.defaults[field]) {
                    this.defaults[field] = column
                }
            })
        },
        dragStart(event, column) {
            event.dataTransfer.setData('text/plain', column)
        },
        handleDrop(event, fieldKey) {
            const column = event.dataTransfer.getData('text/plain')
            this.defaults[fieldKey] = column
            this.validateMappedData()
        },
        nextStep() {
            if (this.currentStep === 2) {
                // Check if required fields are mapped before proceeding to step 3
                const missingRequiredFields = Object.entries(this.mappingFields)
                    .filter(([key, field]) => field.required && !this.defaults[key])
                    .map(([_, field]) => field.label)

                if (missingRequiredFields.length > 0) {
                    this.$toast.error(this.$t('components.templateOptions.requiredFieldsError', { fields: missingRequiredFields.join(', ') }))
                    return
                }
            }

            if (this.currentStep < 3) {
                this.currentStep++
                if (this.currentStep === 3) {
                    this.validateMappedData()
                }
            }
        },
        prevStep() {
            if (this.currentStep > 1) {
                this.currentStep--
            }
        },
        modalActive() {
            // Modal instance'ı varsa önce yok edelim
            if (this.modalInstance) {
                this.modalInstance.dispose()
            }

            const modalElement = document.getElementById('templateOptionsModal')
            if (modalElement) {
                this.modalInstance = new Modal(modalElement, {
                    backdrop: 'static',
                    keyboard: false,
                })
                this.modalInstance.show()

                // Modal kapandığında event'i dinle
                modalElement.addEventListener('hidden.bs.modal', () => {
                    this.$emit('closeModal')
                })
            }
        },
        hideModal() {
            this.$emit('closeModal')
        },
        handleSubmit() {
            this.validateMappedData()
            if (this.hasValidationErrors) {
                return
            }

            // Prepare the data based on the mapping configuration
            const formData = new FormData()

            // Add the file if it exists
            if (this.file && this.file[0]) {
                formData.append('file', this.file[0])
            }

            // Add the mapping options
            Object.entries(this.defaults).forEach(([key, value]) => {
                formData.append(`options[rows][${key}]`, value)
            })

            // Add other required fields
            formData.append('headingRow', this.headingRow.toString())
            formData.append('currency', 'TRY') // You might want to make this dynamic
            formData.append('override', 'true')
            this.$emit('saveTemplateOptions', {
                headingRow: this.headingRow,
                defaults: this.defaults,
                updatedData: this.data,
                formData,
            })

            this.hideModal()
        },
        isColumnDetected(column) {
            if (!column) return false
            return Object.values(this.detectedColumns).includes(column)
        },
        isAutoDetected(fieldKey) {
            return this.detectedColumns[fieldKey] === this.defaults[fieldKey]
        },
        hasFieldError(fieldKey, value) {
            if (!this.validationRules[fieldKey] || !value) return false

            const rule = this.validationRules[fieldKey]
            return !rule.pattern.test(value)
        },
        getFieldErrorMessage(fieldKey, value) {
            if (!this.validationRules[fieldKey] || !value) return null

            const rule = this.validationRules[fieldKey]
            if (!rule.pattern.test(value)) {
                return this.$t(rule.message)
            }

            // Additional error messages for specific validations
            switch (fieldKey) {
                case 'product_cost':
                case 'price':
                    if (parseFloat(value) <= 0) {
                        return this.$t('components.templateOptions.validation.positivePrice')
                    }
                    break
                case 'stock':
                    if (parseInt(value) < 0) {
                        return this.$t('components.templateOptions.validation.nonNegativeStock')
                    }
                    break
            }

            return null
        },
        detectColumnType(column) {
            if (!column) return 'text'
            if (this.columnTypes[column]) return this.columnTypes[column]

            const sampleValues = this.previewMappedData
                .map((row) => row[column])
                .filter(Boolean)
                .slice(0, 5)

            if (sampleValues.length === 0) return 'text'

            const type = this.inferDataType(sampleValues)
            this.columnTypes[column] = type
            return type
        },
        inferDataType(values) {
            const isNumber = values.every((v) => !isNaN(v) && v.toString().match(/^-?\d*\.?\d*$/))
            if (isNumber) return 'number'

            const isDate = values.every((v) => !isNaN(Date.parse(v)))
            if (isDate) return 'date'

            return 'text'
        },
        hasTypeMismatch(fieldKey) {
            const mappedColumn = this.defaults[fieldKey]
            if (!mappedColumn) return false

            const expectedType = this.mappingFields[fieldKey].type
            const actualType = this.detectColumnType(mappedColumn)
            return expectedType && expectedType !== actualType
        },
        resetMapping() {
            this.defaults = Object.keys(this.defaults).reduce((acc, key) => {
                acc[key] = ''
                return acc
            }, {})
            this.validateMappedData()
        },
        clearMapping(fieldKey) {
            this.defaults[fieldKey] = ''
            this.validateMappedData()
        },
        toggleFullPreview() {
            this.showFullPreview = !this.showFullPreview
        },
        isFixedValue(fieldKey, value, rowIndex) {
            return this.fixedValues[fieldKey]?.[rowIndex] !== undefined
        },
        getFixedValue(fieldKey, value, rowIndex) {
            return this.fixedValues[fieldKey]?.[rowIndex]
        },
        async applyBulkFixes() {
            this.isApplyingFixes = true
            try {
                this.fixedValues = {}
                const updatedData = [...this.data]

                // Tüm hatalı alanları düzelt
                for (const [fieldKey] of Object.entries(this.validationErrors)) {
                    await this.applyFieldFixes(fieldKey, updatedData)
                }

                // Emit event to update data
                this.$emit('update:data', updatedData)

                // Validasyonu tekrar kontrol et
                this.validateMappedData()

                // displayedData'yı güncelle
                this.$forceUpdate()
            } catch (error) {
                console.error('Error in applyBulkFixes:', error)
            } finally {
                this.isApplyingFixes = false
            }
        },
        async applyFieldFixes(fieldKey, updatedData = null) {
            if (!this.validationErrors[fieldKey]) return

            if (!updatedData) {
                updatedData = [...this.data]
            }

            if (!this.fixedValues[fieldKey]) {
                this.fixedValues[fieldKey] = {}
            }

            try {
                this.validationErrors[fieldKey].forEach((error) => {
                    if (!error || error.row === undefined) return

                    const value = String(error.value || '')
                    const rule = this.validationRules[fieldKey]

                    if (rule && rule.fix) {
                        const fixedValue = rule.fix(value)

                        if (fixedValue !== value) {
                            const rowIndex = error.row - 1
                            const columnName = this.defaults[fieldKey]
                            const columnIndex = this.columns.indexOf(columnName)

                            if (columnIndex !== -1 && rowIndex >= 0) {
                                const dataIndex = rowIndex + this.headingRow

                                // Veriyi güncelle
                                if (dataIndex < updatedData.length) {
                                    if (!Array.isArray(updatedData[dataIndex])) {
                                        updatedData[dataIndex] = Array.isArray(this.data[dataIndex]) ? [...this.data[dataIndex]] : Object.values(this.data[dataIndex])
                                    }

                                    // Düzeltilen değeri kaydet
                                    updatedData[dataIndex][columnIndex] = fixedValue

                                    // Düzeltilen değeri fixedValues'a ekle
                                    this.fixedValues[fieldKey][rowIndex] = fixedValue
                                    error.fixedValue = fixedValue
                                }
                            }
                        }
                    }
                })

                // Veriyi güncelle
                if (updatedData !== this.data) {
                    this.$emit('update:data', updatedData)
                }

                // Validasyonu tekrar kontrol et
                this.validateMappedData()

                // UI'ı güncelle
                this.$forceUpdate()
            } catch (error) {
                console.error(`Error in applyFieldFixes for field ${fieldKey}:`, error)
            }
        },
        exportValidationReport() {
            const report = {
                timestamp: new Date().toISOString(),
                totalRows: this.data.length - this.headingRow,
                mappedFields: Object.values(this.defaults).filter(Boolean).length,
                errors: this.validationErrors,
                fixedValues: this.fixedValues,
            }
            console.log(report)
        },
        startEditing(rowIndex, fieldKey) {
            this.editingCell = `${rowIndex}-${fieldKey}`
            this.$nextTick(() => {
                if (this.$refs.editInput) {
                    this.$refs.editInput[0]?.focus()
                }
            })
        },
        isEditing(rowIndex, fieldKey) {
            return this.editingCell === `${rowIndex}-${fieldKey}`
        },
        finishEditing(rowIndex, fieldKey) {
            this.editingCell = null

            // Get the actual row index in the main data array (accounting for header row)
            const dataIndex = rowIndex + this.headingRow

            // Get the column name and index
            const columnName = this.defaults[fieldKey]
            const columnIndex = this.columns.indexOf(columnName)

            if (columnIndex !== -1 && dataIndex < this.data.length) {
                // Get the new value from displayedData
                const newValue = this.displayedData[rowIndex][fieldKey]

                // Create a new copy of the data array
                const updatedData = [...this.data]

                // Ensure the row is an array
                if (!Array.isArray(updatedData[dataIndex])) {
                    updatedData[dataIndex] = Object.values(updatedData[dataIndex])
                }

                // Update the value
                updatedData[dataIndex][columnIndex] = newValue

                // Emit the update
                this.$emit('update:data', updatedData)
            }

            this.validateMappedData()
        },
        resetAll() {
            this.currentStep = 1
            this.headingRow = 1
            this.columns = []
            this.defaults = Object.keys(this.defaults).reduce((acc, key) => {
                acc[key] = ''
                return acc
            }, {})
            this.validationErrors = {}
            this.fixedValues = {}
            this.editingCell = null
            this.editedData = {}
            this.showFullPreview = false
        },
    },
    created() {
        // Initialize fields from mappingConfig
        if (this.mappingConfig) {
            this.mappingFields = this.mappingConfig.fields
            this.validationRules = this.mappingConfig.validationRules
            this.columnPatterns = this.mappingConfig.columnPatterns

            // Initialize defaults with empty values
            this.defaults = Object.keys(this.mappingConfig.fields).reduce((acc, key) => {
                acc[key] = ''
                return acc
            }, {})

            // Initialize orderedMappingFields for draggable
            this.orderedMappingFields = Object.entries(this.mappingConfig.fields).map(([key, field]) => ({
                key,
                ...field,
            }))
        }
    },
    mounted() {
        if (this.data && this.data.length > 0 && this.headingRow && this.headingRow > 0) {
            this.columns = this.data[this.headingRow - 1]
            if (this.autoDetectEnabled) {
                this.applyAutoDetection()
            }
        }
    },
    watch: {
        showModal: {
            immediate: true,
            handler(newValue) {
                if (newValue === true) {
                    // Veri değişmiş mi kontrol et
                    const currentDataStr = JSON.stringify(this.data)
                    if (this.lastDataStr !== currentDataStr) {
                        // Veri değişmişse her şeyi resetle
                        this.resetAll()
                        if (this.data && this.data.length > 0) {
                            this.columns = this.data[this.headingRow - 1]?.map((col) => (col === null || col === undefined ? '' : String(col))) || []
                            if (this.autoDetectEnabled) {
                                this.applyAutoDetection()
                            }
                        }
                    } else {
                        // Veri değişmemişse sadece adım 1'e dön
                        this.currentStep = 1
                    }

                    // Son veriyi sakla
                    this.lastDataStr = currentDataStr
                } else if (this.modalInstance) {
                    this.hideModal()
                }
            },
        },
    },
    beforeUnmount() {},
}
</script>

<style scoped>
.modal-header {
    padding: 1rem;
    border-bottom: 1px solid #e7eaf3;
}

.header-selection-container {
    padding: 1rem;
}

.preview-table-container {
    margin-top: 1rem;
    border: 1px solid #e7eaf3;
    border-radius: 0.5rem;
    overflow: auto;
    max-height: 400px;
}

.selected-header {
    background-color: rgba(55, 125, 255, 0.1) !important;
    cursor: pointer;
    font-weight: 600;
}

.check-column {
    width: 40px;
    text-align: center;
    background-color: transparent !important;
}

.preview-table-container tr:hover {
    background-color: rgba(55, 125, 255, 0.05);
    cursor: pointer;
}

.mapping-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    padding: 1rem;
    position: relative;
    height: calc(100vh - 300px);
    overflow: hidden;
}

.source-columns {
    position: sticky;
    top: 0;
    left: 0;
    height: 100%;
    overflow-y: auto;
    padding-right: 1rem;
}

.mapping-fields {
    overflow-y: auto;
    height: 100%;
    padding-right: 1rem;
}

.column-list,
.field-list {
    border: 1px solid #e7eaf3;
    border-radius: 0.5rem;
    padding: 1rem;
    min-height: 300px;
}

/* Scrollbar stilleri */
.source-columns::-webkit-scrollbar,
.mapping-fields::-webkit-scrollbar {
    width: 3px;
}

.source-columns::-webkit-scrollbar-track,
.mapping-fields::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.source-columns::-webkit-scrollbar-thumb,
.mapping-fields::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.source-columns::-webkit-scrollbar-thumb:hover,
.mapping-fields::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.column-item {
    padding: 0.5rem;
    margin: 0.5rem 0;
    background: #f8f9fa;
    border-radius: 0.25rem;
    cursor: move;
}

.field-item {
    padding: 1rem;
    margin: 0.5rem 0;
    border: 2px dashed #e7eaf3;
    border-radius: 0.25rem;
    transition: all 0.3s;
}

.field-item.mapped {
    border-style: solid;
    border-color: #377dff;
    background: #f8f9fa;
}

.field-label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.field-value {
    color: #677788;
    font-size: 0.875rem;
}

.preview-table-wrapper {
    overflow-x: auto;
    max-height: 290px;
    overflow-y: auto;
    border: 1px solid #e7eaf3;
    border-radius: 0.5rem;
}

.preview-table-wrapper table {
    margin-bottom: 0;
}

.preview-table-wrapper thead {
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
}

.alert-soft-primary {
    background-color: rgba(55, 125, 255, 0.1);
    border-color: rgba(55, 125, 255, 0.1);
    color: #377dff;
}

.alert-soft-success {
    background-color: rgba(0, 201, 167, 0.1);
    border-color: rgba(0, 201, 167, 0.1);
    color: #00c9a7;
}

.alert-soft-warning {
    background-color: rgba(255, 193, 7, 0.1);
    border-color: rgba(255, 193, 7, 0.1);
    color: #ffc107;
}

.alert-soft-danger {
    background-color: rgba(237, 76, 120, 0.1);
    border-color: rgba(237, 76, 120, 0.1);
    color: #ed4c78;
}

.validation-errors {
    background: #fff;
    border: 1px solid #e7eaf3;
    border-radius: 0.5rem;
    padding: 1rem;
    max-height: 300px;
    overflow-y: auto;
}

.validation-error-group:not(:last-child) {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e7eaf3;
}

.validation-error-title {
    color: #ed4c78;
    margin-bottom: 0.5rem;
}

.validation-error-list {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
    font-size: 0.875rem;
}

.validation-error-list li {
    margin-bottom: 0.25rem;
}

.invalid-value {
    color: #ed4c78;
    font-family: monospace;
    padding: 0.125rem 0.25rem;
    background: rgba(237, 76, 120, 0.1);
    border-radius: 0.25rem;
}

.has-validation-error {
    background-color: rgba(237, 76, 120, 0.05);
}

.data-stats {
    background: #fff;
    border: 1px solid #e7eaf3;
    border-radius: 0.5rem;
    padding: 1rem;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.stat-item {
    text-align: center;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 0.5rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #677788;
    margin-bottom: 0.5rem;
}

.stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: #377dff;
}

.stat-value.text-danger {
    color: #ed4c78;
}

.is-required {
    background-color: rgba(55, 125, 255, 0.05);
}

.keyboard-shortcuts kbd {
    background-color: #f8f9fa;
    border: 1px solid #e7eaf3;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 0.75rem;
    margin: 0 2px;
}

.detected-count .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

.bg-soft-success {
    background-color: rgba(0, 201, 167, 0.1);
    color: #00c9a7;
}

.column-item.detected {
    border-left: 3px solid #00c9a7;
}

.field-item.required {
    border-left: 3px solid #377dff;
}

.field-item.auto-detected {
    background-color: rgba(55, 125, 255, 0.05);
}

.required-badge {
    color: #377dff;
    margin-left: 4px;
}

.bi-magic {
    font-size: 0.875rem;
}

.bi-check-circle-fill {
    font-size: 0.875rem;
}

.search-box {
    position: relative;
}

.search-box .input-group-text {
    background: transparent;
    border-right: none;
}

.search-box .form-control {
    border-left: none;
}

.search-box .form-control:focus {
    border-color: #e7eaf3;
    box-shadow: none;
}

.column-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.column-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.column-type {
    font-size: 0.75rem;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    background: #e7eaf3;
    color: #677788;
}

.type-number .column-type {
    background: rgba(55, 125, 255, 0.1);
    color: #377dff;
}

.type-date .column-type {
    background: rgba(0, 201, 167, 0.1);
    color: #00c9a7;
}

.field-item {
    display: flex;
    gap: 0.5rem;
}

.field-handle {
    cursor: move;
    color: #677788;
    padding: 0.5rem;
}

.field-content {
    flex: 1;
}

.field-actions {
    display: flex;
    align-items: center;
}

.type-badge {
    font-size: 0.75rem;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    background: #e7eaf3;
    color: #677788;
    margin-left: 0.5rem;
}

.type-mismatch {
    border-color: #ed4c78;
}

.btn-ghost-danger {
    color: #ed4c78;
    background: transparent;
    border: none;
}

.btn-ghost-danger:hover {
    background: rgba(237, 76, 120, 0.1);
}

.preview-actions {
    background: #fff;
    border: 1px solid #e7eaf3;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
}

.btn-soft-primary {
    background-color: rgba(55, 125, 255, 0.1);
    color: #377dff;
    border: none;
}

.btn-soft-primary:hover {
    background-color: rgba(55, 125, 255, 0.2);
}

.btn-soft-secondary {
    background-color: rgba(113, 134, 157, 0.1);
    color: #71869d;
    border: none;
}

.btn-soft-secondary:hover {
    background-color: rgba(113, 134, 157, 0.2);
}

.row-number {
    width: 50px;
    background-color: #f8f9fa;
    text-align: center;
}

.arrow-icon {
    color: #71869d;
}

.fixed-value {
    color: #00c9a7;
    font-weight: 500;
}

.original-value {
    color: #ed4c78;
}

.is-fixed {
    background-color: rgba(0, 201, 167, 0.05);
}

.validation-error-group .btn-xs {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

.column-item.is-empty {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f1f1f1;
    pointer-events: none;
}

.editable-cell {
    cursor: pointer;
    position: relative;
}

.editable-cell:hover {
    background-color: rgba(55, 125, 255, 0.05);
}

.editable-cell .form-control {
    padding: 0.25rem;
    height: auto;
    width: 100%;
}

.cell-content {
    min-height: 24px;
    padding: 2px;
}

.validation-summary {
    font-size: 0.875rem;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(237, 76, 120, 0.2);
}

.validation-field-count {
    display: inline-flex;
    align-items: center;
    margin-right: 1rem;
    margin-bottom: 0.25rem;
}

.validation-field-count .field-name {
    font-weight: 500;
    margin-right: 0.25rem;
}

.validation-field-count .error-count {
    color: #ed4c78;
}
::-webkit-scrollbar {
    width: 3px;
    height: 3px;
}
</style>
